import React, { useState, useEffect } from 'react';
import './BarrysTab.css';

interface SectionAvailability {
  available: number;
  total: number;
}

interface ClassAvailability {
  treadmill: SectionAvailability;
  floor: SectionAvailability;
  doubleFloor: SectionAvailability;
  mySpot?: string;
}

interface BarrysClass {
  classId: string;
  date: string;
  dayOfWeek: string;
  time: string;
  className: string;
  instructor: string;
  instructorId: string;
  duration: string;
  room: string;
  isBooked: boolean;
  isBookable: boolean;
  bookingUrl?: string;
  availability?: ClassAvailability;
}

interface EnrolledClass {
  dateTime: string;
  date: string;
  time: string;
  dayOfWeek: string;
  spot: string;
  className: string;
  instructor: string;
  location: string;
  status: 'enrolled' | 'cancelled' | 'noshow';
  statusText: string;
}

// Unified display item combining schedule + enrolled data
interface DisplayClass {
  key: string;
  classId?: string;
  date: string;
  dayOfWeek: string;
  time: string;
  className: string;
  instructor: string;
  duration?: string;
  spot?: string;
  status: 'booked' | 'available' | 'enrolled-past' | 'cancelled' | 'noshow';
  source: 'schedule' | 'enrolled';
  availability?: ClassAvailability;
}

interface BarrysTabProps {
  refreshTrigger?: number;
  initialFilter?: FilterMode;
}

type FilterMode = 'all' | 'ariel' | 'myClasses';

export const BarrysTab: React.FC<BarrysTabProps> = ({ refreshTrigger, initialFilter }) => {
  const [classes, setClasses] = useState<BarrysClass[]>([]);
  const [enrolled, setEnrolled] = useState<EnrolledClass[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterMode>(initialFilter || 'all');

  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [bookingClassId, setBookingClassId] = useState<string | null>(null);

  const fetchSchedule = async () => {
    setIsLoading(true);
    setError('');
    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/barrys/schedule', { headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setClasses(data.classes || []);
      setEnrolled(data.enrolled || []);
      setLastSyncTime(data.lastSyncTime || null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch schedule');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerSync = async () => {
    setIsSyncing(true);
    setError('');
    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/barrys/sync', { method: 'POST', headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setClasses(data.classes || []);
      setEnrolled(data.enrolled || []);
      setLastSyncTime(data.lastSyncTime || null);
    } catch (err: any) {
      setError(err.message || 'Failed to sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const bookClass = async (classId: string) => {
    setBookingClassId(classId);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/barrys/book', {
        method: 'POST',
        headers,
        body: JSON.stringify({ classId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.success) {
        // Re-sync to get updated data
        await triggerSync();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to book class');
    } finally {
      setBookingClassId(null);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, [refreshTrigger]);

  // Build unified display list from schedule + enrolled classes
  const buildDisplayClasses = (): DisplayClass[] => {
    const items: DisplayClass[] = [];
    const today = new Date().toISOString().split('T')[0];

    // Build a map of enrolled classes to cross-reference with schedule
    const enrolledMap = new Map<string, EnrolledClass>();
    for (const c of enrolled) {
      if (c.status === 'enrolled' && c.date) {
        enrolledMap.set(`${c.date}|${c.time}|${c.instructor}`, c);
      }
    }

    // Add schedule classes: only booked, or available with availability data (today/future)
    for (const cls of classes) {
      const enrolledMatch = enrolledMap.get(`${cls.date}|${cls.time}|${cls.instructor}`);
      const isBooked = cls.isBooked || !!enrolledMatch;

      if (!isBooked && !cls.availability) continue;
      if (!isBooked && cls.date < today) continue;

      items.push({
        key: `sched-${cls.classId}`,
        classId: cls.classId,
        date: cls.date,
        dayOfWeek: cls.dayOfWeek,
        time: cls.time,
        className: cls.className,
        instructor: cls.instructor,
        duration: cls.duration,
        spot: enrolledMatch?.spot,
        status: isBooked ? 'booked' : 'available',
        source: 'schedule',
        availability: cls.availability,
      });
    }

    // Add enrolled classes not already in schedule
    const seenKeys = new Set(items.map((i) => `${i.date}|${i.time}|${i.instructor}`));

    for (const cls of enrolled) {
      if (!cls.date) continue;
      if (cls.status === 'cancelled' || cls.status === 'noshow') continue;
      const dedupKey = `${cls.date}|${cls.time}|${cls.instructor}`;
      if (seenKeys.has(dedupKey)) continue;
      seenKeys.add(dedupKey);

      items.push({
        key: `enrolled-${cls.date}-${cls.time}-${cls.instructor}`,
        date: cls.date,
        dayOfWeek: cls.dayOfWeek,
        time: cls.time,
        className: cls.className,
        instructor: cls.instructor,
        spot: cls.spot,
        status: cls.date >= today ? 'booked' : 'enrolled-past',
        source: 'enrolled',
      });
    }

    // Sort by date descending (most recent/upcoming first)
    items.sort((a, b) => b.date.localeCompare(a.date));

    return items;
  };

  const applyFilter = (items: DisplayClass[]): DisplayClass[] => {
    if (filter === 'ariel') return items.filter((c) => c.instructor.toLowerCase().includes('ariel'));
    if (filter === 'myClasses') return items.filter((c) => c.status !== 'available');
    return items;
  };

  const groupByDate = (items: DisplayClass[]): Map<string, DisplayClass[]> => {
    const groups = new Map<string, DisplayClass[]>();
    for (const item of items) {
      if (!groups.has(item.date)) groups.set(item.date, []);
      groups.get(item.date)!.push(item);
    }
    return groups;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatRelativeTime = (isoStr: string) => {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getStatusBadge = (status: DisplayClass['status']) => {
    switch (status) {
      case 'booked':
        return <span className="barrys-tab__class-badge barrys-tab__class-badge--booked">Booked</span>;
      case 'available':
        return <span className="barrys-tab__class-badge barrys-tab__class-badge--available">Available</span>;
      case 'enrolled-past':
        return <span className="barrys-tab__class-badge barrys-tab__class-badge--attended">Attended</span>;
      case 'cancelled':
        return <span className="barrys-tab__class-badge barrys-tab__class-badge--cancelled">Cancelled</span>;
      case 'noshow':
        return <span className="barrys-tab__class-badge barrys-tab__class-badge--noshow">No Show</span>;
    }
  };

  const getClassModifier = (item: DisplayClass) => {
    const mods: string[] = [];
    if (item.status === 'booked') mods.push('barrys-tab__class--booked');
    if (item.status === 'available') mods.push('barrys-tab__class--available');
    if (item.status === 'cancelled' || item.status === 'noshow') mods.push('barrys-tab__class--dimmed');
    return mods.join(' ');
  };

  if (isLoading) {
    return (
      <div className="barrys-tab">
        <div className="barrys-tab__loading">
          <div className="barrys-tab__loading-icon">💪</div>
          <p>Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="barrys-tab">
        <div className="barrys-tab__error">
          <p>{error}</p>
        </div>
        <div className="barrys-tab__actions">
          <button className="barrys-tab__button" onClick={fetchSchedule}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const allItems = buildDisplayClasses();
  const filtered = applyFilter(allItems);
  const grouped = groupByDate(filtered);

  if (allItems.length === 0) {
    return (
      <div className="barrys-tab">
        <div className="barrys-tab__empty">
          <div className="barrys-tab__empty-icon">💪</div>
          <p className="barrys-tab__empty-text">No classes found</p>
          <p className="barrys-tab__empty-subtext">
            Connect your Barry&apos;s account and click Sync to fetch the schedule.
          </p>
          <button
            className="barrys-tab__button"
            onClick={triggerSync}
            disabled={isSyncing}
            style={{ marginTop: 'var(--space-md)' }}
          >
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>
    );
  }

  const bookedCount = allItems.filter((i) => i.status === 'booked').length;
  const arielCount = allItems.filter((i) => i.instructor.toLowerCase().includes('ariel')).length;

  return (
    <div className="barrys-tab">
      <div className="barrys-tab__actions">
        <div className="barrys-tab__filters">
          <button
            className={`barrys-tab__filter${filter === 'all' ? ' barrys-tab__filter--active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({allItems.length})
          </button>
          <button
            className={`barrys-tab__filter${filter === 'ariel' ? ' barrys-tab__filter--active' : ''}`}
            onClick={() => setFilter('ariel')}
          >
            Ariel ({arielCount})
          </button>
          <button
            className={`barrys-tab__filter${filter === 'myClasses' ? ' barrys-tab__filter--active' : ''}`}
            onClick={() => setFilter('myClasses')}
          >
            My Classes ({bookedCount})
          </button>
        </div>
        <div className="barrys-tab__sync-group">
          {lastSyncTime && <span className="barrys-tab__sync-time">Synced {formatRelativeTime(lastSyncTime)}</span>}
          <button className="barrys-tab__button" onClick={triggerSync} disabled={isSyncing}>
            {isSyncing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      {Array.from(grouped.entries()).map(([date, dayClasses]) => (
        <div key={date} className="barrys-tab__day">
          <h4 className="barrys-tab__day-header">
            {dayClasses[0].dayOfWeek}
            <span className="barrys-tab__day-date">{formatDate(date)}</span>
          </h4>
          <div className="barrys-tab__classes">
            {dayClasses.map((item) => (
              <div key={item.key} className={`barrys-tab__class ${getClassModifier(item)}`}>
                <span className="barrys-tab__class-time">{item.time}</span>
                <div className="barrys-tab__class-info">
                  <div className="barrys-tab__class-name">{item.className}</div>
                  <div className="barrys-tab__class-meta">
                    <span>{item.instructor}</span>
                    {item.duration && <span>{item.duration}</span>}
                    {item.spot && <span>Spot {item.spot}</span>}
                  </div>
                  {item.availability && (
                    <div className="barrys-tab__availability">
                      <span className="barrys-tab__avail-label barrys-tab__avail-label--full">Spots Taken</span>
                      <span className="barrys-tab__avail-label barrys-tab__avail-label--short">Taken</span>
                      <span className={item.availability.treadmill.available > 0 ? 'barrys-tab__avail--has-spots' : ''}>
                        T {item.availability.treadmill.total - item.availability.treadmill.available}/
                        {item.availability.treadmill.total}
                      </span>
                      <span className={item.availability.floor.available > 0 ? 'barrys-tab__avail--has-spots' : ''}>
                        F {item.availability.floor.total - item.availability.floor.available}/
                        {item.availability.floor.total}
                      </span>
                      <span
                        className={item.availability.doubleFloor.available > 0 ? 'barrys-tab__avail--has-spots' : ''}
                      >
                        DF {item.availability.doubleFloor.total - item.availability.doubleFloor.available}/
                        {item.availability.doubleFloor.total}
                      </span>
                    </div>
                  )}
                </div>
                <div className="barrys-tab__class-actions">
                  {getStatusBadge(item.status)}
                  {item.status === 'available' &&
                    item.classId &&
                    item.availability &&
                    item.availability.doubleFloor.available > 0 &&
                    item.date >= new Date().toISOString().split('T')[0] && (
                      <button
                        className="barrys-tab__book-button"
                        onClick={() => bookClass(item.classId!)}
                        disabled={bookingClassId === item.classId}
                      >
                        {bookingClassId === item.classId ? 'Booking...' : 'Book DF'}
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
